import React, { useEffect, useState } from 'react';
import { Users, LogOut, TrendingUp, BarChart3, Download, Activity, Lightbulb } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { adminStats, adminLogout, adminPosthogOverview, downloadUsersCsv, listFeatureRequests } from '../../lib/adminApi';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return iso.slice(0, 10);
  }
}

export default function AdminDashboard({ username, onLoggedOut }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [posthog, setPosthog] = useState(null);
  const [posthogError, setPosthogError] = useState(null);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [featureRequests, setFeatureRequests] = useState(null);
  const [featureRequestsError, setFeatureRequestsError] = useState(null);

  useEffect(() => {
    adminStats().then(setStats).catch((e) => setError(e.message || 'Could not load stats.'));
    adminPosthogOverview().then(setPosthog).catch((e) => setPosthogError(e.message || 'Could not load PostHog data.'));
    listFeatureRequests().then(setFeatureRequests).catch((e) => setFeatureRequestsError(e.message || 'Could not load feature requests.'));
  }, []);

  const handleLogout = async () => {
    await adminLogout();
    onLoggedOut();
  };

  const handleExportCsv = async () => {
    setCsvDownloading(true);
    try {
      await downloadUsersCsv();
    } catch (e) {
      setError(e.message || 'Could not export CSV.');
    } finally {
      setCsvDownloading(false);
    }
  };

  const last7 = stats ? stats.signups_by_day.slice(-14) : [];
  const last30Total = stats ? stats.signups_by_day.slice(-30).reduce((sum, d) => sum + d.count, 0) : 0;

  return (
    <div className="min-h-screen bg-[#0b0d10] text-white">
      <header className="border-b border-white/10 px-6 h-16 flex items-center justify-between">
        <div>
          <h1 className="text-sm font-semibold tracking-tight">Waxify Admin</h1>
          <p className="text-xs text-white/40">Signed in as {username}</p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors px-3 py-1.5 rounded-full hover:bg-white/5"
          data-testid="admin-logout-button"
        >
          <LogOut className="w-3.5 h-3.5" /> Log out
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {error && <p className="text-sm text-red-400" data-testid="admin-stats-error">{error}</p>}

        {stats && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="bg-[#14171c] border-white/10 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-white/50 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> Total signups
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-semibold" data-testid="admin-total-users">{stats.total_users}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#14171c] border-white/10 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-white/50 flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5" /> Last 30 days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-semibold" data-testid="admin-last-30-days">{last30Total}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#14171c] border-white/10 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-white/50 flex items-center gap-1.5">
                    <BarChart3 className="w-3.5 h-3.5" /> Active days tracked
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-semibold">{stats.signups_by_day.length}</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#14171c] border-white/10 text-white" data-testid="admin-signups-chart">
              <CardHeader>
                <CardTitle className="text-sm">Signups over time</CardTitle>
              </CardHeader>
              <CardContent>
                {stats.signups_by_day.length === 0 ? (
                  <p className="text-sm text-white/40">No signups yet.</p>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={last7}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }} />
                        <Tooltip contentStyle={{ background: '#14171c', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }} />
                        <Line type="monotone" dataKey="count" stroke="#ea580c" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#14171c] border-white/10 text-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm">Signed-up users</CardTitle>
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={csvDownloading || stats.users.length === 0}
                  className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white disabled:opacity-30 transition-colors px-2.5 py-1.5 rounded-md hover:bg-white/5"
                  data-testid="admin-export-csv-button"
                >
                  <Download className="w-3.5 h-3.5" /> {csvDownloading ? 'Exporting…' : 'Export CSV'}
                </button>
              </CardHeader>
              <CardContent>
                {stats.users.length === 0 ? (
                  <p className="text-sm text-white/40">No signups yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-users-table">
                      <thead>
                        <tr className="text-left text-white/40 text-xs border-b border-white/10">
                          <th className="py-2 pr-4">Name</th>
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Signed up</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.users.map((u) => (
                          <tr key={u.email} className="border-b border-white/5">
                            <td className="py-2 pr-4">{u.name || '—'}</td>
                            <td className="py-2 pr-4 text-white/70">{u.email}</td>
                            <td className="py-2 pr-4 text-white/50">{formatDate(u.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        <Card className="bg-[#14171c] border-white/10 text-white" data-testid="admin-posthog-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" /> PostHog analytics (last 30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {posthogError ? (
              <p className="text-sm text-white/50" data-testid="admin-posthog-error">{posthogError}</p>
            ) : !posthog ? (
              <p className="text-sm text-white/40">Loading…</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-white/40">Total events</p>
                    <p className="text-2xl font-semibold" data-testid="admin-posthog-events">{posthog.events_last_30_days}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/40">Unique people</p>
                    <p className="text-2xl font-semibold" data-testid="admin-posthog-unique">{posthog.unique_people_last_30_days}</p>
                  </div>
                </div>
                {posthog.daily_events.length > 0 && (
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={posthog.daily_events}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }} />
                        <Tooltip contentStyle={{ background: '#14171c', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }} />
                        <Line type="monotone" dataKey="events" stroke="#60a5fa" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {posthog.events_last_30_days === 0 && (
                  <p className="text-xs text-white/40">
                    No events yet — the app isn't sending browser events to this PostHog project. Add your PostHog public token to start collecting page-view data.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#14171c] border-white/10 text-white" data-testid="admin-feature-requests-card">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5" /> Feature requests {featureRequests ? `(${featureRequests.length})` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {featureRequestsError ? (
              <p className="text-sm text-white/50" data-testid="admin-feature-requests-error">{featureRequestsError}</p>
            ) : !featureRequests ? (
              <p className="text-sm text-white/40">Loading…</p>
            ) : featureRequests.length === 0 ? (
              <p className="text-sm text-white/40">No feature requests yet.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="admin-feature-requests-list">
                {featureRequests.map((fr) => (
                  <div key={fr.id} className="border-b border-white/5 pb-3 last:border-b-0 last:pb-0">
                    <p className="text-sm text-white/90 whitespace-pre-wrap">{fr.message}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-white/40">
                      <span>{formatDate(fr.created_at)}</span>
                      {fr.email && <span className="text-white/50">{fr.email}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
